import type { WatchQueryFetchPolicy } from "@apollo/client/core";
import { useLazyQuery } from "@apollo/client/react";
import { useCallback, useEffect, useState } from "react";

import { SubscriptionTiersByCreatorPublicKeyDocument } from "@/graphql/codegen/graphql";
import { parseTier, type TierSchema } from "@/utils/subscriptions";

export const useGetSubscriptionTiers = ({
  fetchPolicy,
}: {
  fetchPolicy?: WatchQueryFetchPolicy;
} = {}) => {
  const [tiers, setTiers] = useState<TierSchema[]>([]);
  const [
    _getSubscriptionTiers,
    {
      data: subscriptionTiersData,
      loading: loadingSubscriptionTiers,
      refetch: refetchSubscriptionTiers,
    },
  ] = useLazyQuery(SubscriptionTiersByCreatorPublicKeyDocument, {
    ...(fetchPolicy ? { fetchPolicy } : {}),
  });

  const getSubscriptionTiers = useCallback(
    (...args: Parameters<typeof _getSubscriptionTiers>) => {
      const request = _getSubscriptionTiers(...args);
      request.retain?.();
      return request;
    },
    [_getSubscriptionTiers],
  );

  useEffect(() => {
    const tiers = (subscriptionTiersData?.subscriptionTiers?.nodes ?? [])
      ?.filter((t): t is NonNullable<typeof t> => !!t)
      .map(parseTier)
      .sort((a, b) => a.costUsdCents - b.costUsdCents);

    setTiers(tiers);
  }, [subscriptionTiersData]);

  return {
    tiers,
    subscriptionTiersData,
    refetchSubscriptionTiers,
    loadingSubscriptionTiers,
    getSubscriptionTiers,
  };
};
